import { formatBytes, WOW_REGIONS, type CustomTheme, type Theme } from '@moussenaires/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { adminApi } from '../api/admin.js';
import { ApiClientError } from '../api/client.js';
import { useAuth } from '../context/AuthContext.js';
import { ThemePicker } from '../components/ThemePicker.js';
import './AdminPage.css';

type Tab = 'users' | 'settings';

export function AdminPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('users');

  return (
    <div>
      <div className="page-header">
        <h1>{t('admin.title')}</h1>
      </div>

      <div className="admin-tabs">
        <button
          type="button"
          className={`admin-tab ${tab === 'users' ? 'admin-tab-active' : ''}`}
          onClick={() => setTab('users')}
        >
          {t('admin.users')}
        </button>
        <button
          type="button"
          className={`admin-tab ${tab === 'settings' ? 'admin-tab-active' : ''}`}
          onClick={() => setTab('settings')}
        >
          {t('admin.settings')}
        </button>
      </div>

      {tab === 'users' ? <AdminUsersTab /> : <AdminSettingsTab />}
    </div>
  );
}

function AdminUsersTab() {
  const { t } = useTranslation();
  const { user: me, login } = useAuth();
  const queryClient = useQueryClient();
  const usersQuery = useQuery({ queryKey: ['admin', 'users'], queryFn: adminApi.users });

  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });

  const createMutation = useMutation({
    mutationFn: adminApi.createUser,
    onSuccess: () => {
      setNewUsername('');
      setNewPassword('');
      setCreateError(null);
      invalidate();
    },
    onError: (error: unknown) => {
      setCreateError(
        error instanceof ApiClientError ? t(`errors.${error.code}`) : t('common.somethingWrong'),
      );
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      adminApi.updateUser(id, { isActive }),
    onSuccess: invalidate,
  });
  const toggleRoleMutation = useMutation({
    mutationFn: ({ id, role }: { id: number; role: 'member' | 'admin' }) =>
      adminApi.updateUser(id, { role }),
    onSuccess: invalidate,
  });
  const impersonateMutation = useMutation({
    mutationFn: (id: number) => adminApi.impersonate(id),
    onSuccess: (response) => login(response.token, true),
  });

  return (
    <div>
      <form
        className="admin-create-user card"
        onSubmit={(event) => {
          event.preventDefault();
          createMutation.mutate({ username: newUsername.trim(), password: newPassword });
        }}
      >
        <h3>{t('admin.addUser')}</h3>
        <div className="admin-create-user-fields">
          <input
            placeholder={t('auth.username')}
            value={newUsername}
            onChange={(event) => setNewUsername(event.target.value)}
          />
          <input
            placeholder={t('auth.password')}
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!newUsername.trim() || !newPassword || createMutation.isPending}
          >
            {t('admin.addUser')}
          </button>
        </div>
        {createError && <p className="field-error">{createError}</p>}
      </form>

      <div className="admin-user-list">
        {usersQuery.data?.map((user) => (
          <div key={user.id} className="admin-user-row card">
            <div className="admin-user-identity">
              <strong>{user.displayName}</strong>
              <span>@{user.username}</span>
            </div>
            <div className="admin-user-meta">
              <span className="badge">
                {user.role === 'admin' ? t('admin.adminRole') : t('admin.member')}
              </span>
              <span className="badge">
                {user.isActive ? t('admin.active') : t('admin.inactive')}
              </span>
              <span className="admin-user-storage">{formatBytes(user.storageBytes)}</span>
            </div>
            <div className="admin-user-actions">
              {user.id !== me?.id && (
                <>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() =>
                      toggleRoleMutation.mutate({
                        id: user.id,
                        role: user.role === 'admin' ? 'member' : 'admin',
                      })
                    }
                  >
                    {user.role === 'admin' ? t('admin.demote') : t('admin.promote')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() =>
                      toggleActiveMutation.mutate({ id: user.id, isActive: !user.isActive })
                    }
                  >
                    {user.isActive ? t('admin.deactivate') : t('admin.activate')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => impersonateMutation.mutate(user.id)}
                  >
                    {t('admin.impersonate')}
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminSettingsTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({ queryKey: ['admin', 'settings'], queryFn: adminApi.settings });

  const [siteTitle, setSiteTitle] = useState('');
  const [storageLimitGb, setStorageLimitGb] = useState('');
  const [publicRoster, setPublicRoster] = useState(true);
  const [wowRegion, setWowRegion] = useState<(typeof WOW_REGIONS)[number]>('eu');
  const [warcraftlogsZoneId, setWarcraftlogsZoneId] = useState('');
  const [defaultTheme, setDefaultTheme] = useState<Theme>('dark');
  const [defaultThemeCustom, setDefaultThemeCustom] = useState<CustomTheme>({});
  const [initialised, setInitialised] = useState(false);

  if (settingsQuery.data && !initialised) {
    setSiteTitle(settingsQuery.data.siteTitle);
    setStorageLimitGb((settingsQuery.data.storageLimitBytes / 1024 ** 3).toFixed(1));
    setPublicRoster(settingsQuery.data.publicRoster);
    setWowRegion(settingsQuery.data.wowRegion);
    setWarcraftlogsZoneId(settingsQuery.data.warcraftlogsZoneId?.toString() ?? '');
    setDefaultTheme(settingsQuery.data.defaultTheme);
    setDefaultThemeCustom(settingsQuery.data.defaultThemeCustom ?? {});
    setInitialised(true);
  }

  const updateMutation = useMutation({
    mutationFn: adminApi.updateSettings,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] }),
  });

  return (
    <div className="card admin-settings-card">
      <div className="field">
        <label htmlFor="admin-site-title">{t('admin.siteTitle')}</label>
        <input
          id="admin-site-title"
          value={siteTitle}
          onChange={(event) => setSiteTitle(event.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="admin-storage-limit">{t('admin.storageLimit')} (Go)</label>
        <input
          id="admin-storage-limit"
          type="number"
          min="0.1"
          step="0.1"
          value={storageLimitGb}
          onChange={(event) => setStorageLimitGb(event.target.value)}
        />
        <span className="admin-settings-hint">{t('admin.storageLimitHint')}</span>
      </div>

      <label className="admin-settings-toggle">
        <input
          type="checkbox"
          checked={publicRoster}
          onChange={(event) => setPublicRoster(event.target.checked)}
        />
        <span>
          {t('admin.publicRoster')}
          <small>{t('admin.publicRosterHint')}</small>
        </span>
      </label>

      <div className="field">
        <label htmlFor="admin-wow-region">{t('admin.wowRegion')}</label>
        <select
          id="admin-wow-region"
          value={wowRegion}
          onChange={(event) => setWowRegion(event.target.value as (typeof WOW_REGIONS)[number])}
        >
          {WOW_REGIONS.map((region) => (
            <option key={region} value={region}>
              {region.toUpperCase()}
            </option>
          ))}
        </select>
        <span className="admin-settings-hint">{t('admin.wowRegionHint')}</span>
      </div>

      <div className="field">
        <label htmlFor="admin-wcl-zone">{t('admin.warcraftlogsZoneId')}</label>
        <input
          id="admin-wcl-zone"
          type="number"
          min="1"
          placeholder={t('admin.warcraftlogsZoneIdPlaceholder')}
          value={warcraftlogsZoneId}
          onChange={(event) => setWarcraftlogsZoneId(event.target.value)}
        />
        <span className="admin-settings-hint">{t('admin.warcraftlogsZoneIdHint')}</span>
      </div>

      <div className="field">
        <label>{t('admin.defaultTheme')}</label>
        <span className="admin-settings-hint">{t('admin.defaultThemeHint')}</span>
        <ThemePicker
          theme={defaultTheme}
          customTheme={defaultThemeCustom}
          onSelectTheme={setDefaultTheme}
          onCustomColorChange={(key, value) =>
            setDefaultThemeCustom((current) => ({ ...current, [key]: value }))
          }
        />
      </div>

      <button
        type="button"
        className="btn btn-primary"
        disabled={updateMutation.isPending}
        onClick={() =>
          updateMutation.mutate({
            siteTitle: siteTitle.trim(),
            storageLimitBytes: Math.round(Number(storageLimitGb) * 1024 ** 3),
            publicRoster,
            wowRegion,
            warcraftlogsZoneId: warcraftlogsZoneId.trim() ? Number(warcraftlogsZoneId) : null,
            defaultTheme,
            defaultThemeCustom: defaultTheme === 'custom' ? defaultThemeCustom : null,
          })
        }
      >
        {t('common.save')}
      </button>
      {updateMutation.isSuccess && <p className="settings-success">{t('settings.saved')}</p>}
      {updateMutation.error instanceof ApiClientError && (
        <p className="field-error">{t(`errors.${updateMutation.error.code}`)}</p>
      )}
    </div>
  );
}
